import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AdvisoryRowLeave } from "@/components/AdvisoryRows";
import { api, formatError } from "@/lib/api";
import { Card } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Check, X, ChevronLeft, ChevronRight, CalendarDays, List as ListIcon, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LeaveSimulationPill } from "@/pages/LeaveSimulationTab";

const STATUS_COLOR = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-slate-200 text-slate-600",
};

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function ymd(d) {
  const y = d.getFullYear(); const m = String(d.getMonth()+1).padStart(2,"0"); const day = String(d.getDate()).padStart(2,"0");
  return `${y}-${m}-${day}`;
}
function monthGrid(year, month /* 0-based */) {
  // Returns 6 rows × 7 cols of Date objects starting from Monday.
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7; // Mon=0
  const start = new Date(year, month, 1 - offset);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start); d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
}

export default function LeavePage() {
  const { user } = useAuth();
  const isAdmin = user.role === "company_admin" || user.role === "team_admin";
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [view, setView] = useState("list"); // "list" | "calendar"
  // Phase 2Z.f — quick status filter for the list view.
  // "all" preserves the existing default behaviour.
  const [statusFilter, setStatusFilter] = useState("all");
  const today = new Date();
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [selectedDay, setSelectedDay] = useState(null); // ISO yyyy-mm-dd
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    employee_id: "", category_id: "", start_date: "", end_date: "", half_day: "none", notes: "",
  });
  // set of leave_request_ids that currently have a pending
  // leave-decision draft. Read-only pill in the row links to /app/drafts.
  const [pendingDraftIds, setPendingDraftIds] = useState(new Set());

  const load = async () => {
    const [lv, cat, bh] = await Promise.all([
      api.get("/leave-requests"),
      api.get("/categories"),
      api.get("/bank-holidays").catch(() => ({ data: [] })),
    ]);
    setRows(lv.data);
    setCategories(cat.data.filter(c => c.kind === "leave" && c.is_active));
    setHolidays(bh.data || []);
    if (isAdmin) {
      const emp = await api.get("/employees");
      setEmployees(emp.data);
      try {
        const dr = await api.get("/drafts/leave/pending-ids");
        setPendingDraftIds(new Set(dr.data?.leave_request_ids || []));
      } catch { setPendingDraftIds(new Set()); }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = {...form};
      if (!isAdmin) delete body.employee_id;
      await api.post("/leave-requests", body);
      toast.success(isAdmin ? "Leave added & approved" : "Leave request submitted");
      setOpen(false);
      setForm({ employee_id: "", category_id: "", start_date: "", end_date: "", half_day: "none", notes: "" });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const decide = async (lv, decision) => {
    try {
      await api.patch(`/leave-requests/${lv.leave_id}`, { decision });
      toast.success(decision === "approved" ? "Approved" : "Rejected");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const cancel = async (lv) => {
    if (!window.confirm("Cancel this leave request?")) return;
    try { await api.delete(`/leave-requests/${lv.leave_id}`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const empName = (id) => employees.find(e => e.employee_id === id)?.name || id;

  /** Lookup tables for the calendar: day → list of leaves / list of holidays. */
  const leavesByDay = useMemo(() => {
    const map = {};
    for (const lv of rows) {
      const a = new Date(lv.start_date); const b = new Date(lv.end_date);
      // Iterate the inclusive date range.
      for (let d = new Date(a); d <= b; d.setDate(d.getDate() + 1)) {
        const key = ymd(d);
        (map[key] = map[key] || []).push(lv);
      }
    }
    return map;
  }, [rows]);
  const holidaysByDay = useMemo(() => {
    const map = {};
    for (const h of holidays) {
      if (h.is_active === false) continue;
      (map[h.date] = map[h.date] || []).push(h);
    }
    return map;
  }, [holidays]);

  const monthCells = useMemo(() => monthGrid(calYear, calMonth), [calYear, calMonth]);
  const stepMonth = (n) => {
    const d = new Date(calYear, calMonth + n, 1);
    setCalYear(d.getFullYear()); setCalMonth(d.getMonth());
  };

  // Counts per status drive both the filter pill labels and the filter result.
  const statusCounts = useMemo(() => {
    const counts = { all: rows.length, pending: 0, approved: 0, rejected: 0 };
    for (const lv of rows) {
      if (counts[lv.status] !== undefined) counts[lv.status] += 1;
    }
    return counts;
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (statusFilter === "all") return rows;
    return rows.filter((lv) => lv.status === statusFilter);
  }, [rows, statusFilter]);

  // Soft pill palette aligned with the existing status badges.
  const FILTER_BUTTONS = [
    { key: "all", label: "All",
      active: "bg-slate-100 text-slate-800 border-slate-300",
      idle: "bg-white text-[#4B5773] border-slate-200 hover:bg-slate-50" },
    { key: "pending", label: "Pending",
      active: "bg-amber-100 text-amber-800 border-amber-300",
      idle: "bg-white text-amber-800 border-amber-200 hover:bg-amber-50" },
    { key: "approved", label: "Approved",
      active: "bg-emerald-100 text-emerald-800 border-emerald-300",
      idle: "bg-white text-emerald-800 border-emerald-200 hover:bg-emerald-50" },
    { key: "rejected", label: "Rejected",
      active: "bg-rose-100 text-rose-800 border-rose-300",
      idle: "bg-white text-rose-800 border-rose-200 hover:bg-rose-50" },
  ];

  return (
    <div>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="eyebrow">Time off</div>
          <h1 className="font-display text-4xl mt-1">Leave requests</h1>
          <p className="text-sm text-[#4B5773] mt-2 max-w-xl">
            Approved leave automatically blocks roster capacity for that day.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* V1: Engine preview hidden — super-admin only diagnostic. */}
          {user?.role === "super_admin" && (
            <Link to={`/app/automation#leave`}
              data-testid="leave-engine-preview-link"
              className="inline-flex items-center text-xs px-2.5 py-1 rounded-full border border-slate-200 bg-white text-[#1D4ED8] hover:bg-[#F6F9FE]">
              🔍 Engine preview
            </Link>
          )}
          {/* View toggle */}
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="leave-view-toggle">
            <button type="button" onClick={()=>setView("list")} data-testid="leave-view-list"
              className={`px-3 py-1.5 text-xs rounded-md inline-flex items-center gap-1.5 ${view === "list" ? "bg-[#DBE7FE] text-[#1D4ED8]" : "text-[#4B5773] hover:bg-slate-50"}`}>
              <ListIcon className="h-3.5 w-3.5"/>List
            </button>
            <button type="button" onClick={()=>setView("calendar")} data-testid="leave-view-calendar"
              className={`px-3 py-1.5 text-xs rounded-md inline-flex items-center gap-1.5 ${view === "calendar" ? "bg-[#DBE7FE] text-[#1D4ED8]" : "text-[#4B5773] hover:bg-slate-50"}`}>
              <CalendarDays className="h-3.5 w-3.5"/>Calendar
            </button>
          </div>
          <Button onClick={()=>setOpen(true)} data-testid="add-leave-btn" className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
            <Plus className="h-4 w-4 mr-1"/>{isAdmin ? "Add leave" : "Request leave"}
          </Button>
        </div>
      </div>

      {view === "calendar" ? (
        <Card>
          {/* Calendar header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={()=>stepMonth(-1)} data-testid="leave-cal-prev"><ChevronLeft className="h-4 w-4"/></Button>
              <div className="font-display text-xl" data-testid="leave-cal-title">{MONTHS[calMonth]} {calYear}</div>
              <Button variant="ghost" size="sm" onClick={()=>stepMonth(1)} data-testid="leave-cal-next"><ChevronRight className="h-4 w-4"/></Button>
              <Button variant="ghost" size="sm" onClick={()=>{ setCalMonth(today.getMonth()); setCalYear(today.getFullYear()); }}
                data-testid="leave-cal-today" className="text-[#2563EB]">Today</Button>
            </div>
            <div className="flex items-center gap-3 text-[11px] text-[#4B5773]">
              <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-[#F97316]"/>Approved leave / Bank holiday</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm border-2 border-amber-400"/>Pending leave</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-slate-200"/>No activity</span>
            </div>
          </div>

          {/* Day labels */}
          <div className="grid grid-cols-7 gap-1.5 mb-1.5">
            {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => (
              <div key={d} className="text-[10px] uppercase tracking-widest text-[#4B5773] text-center py-1">{d}</div>
            ))}
          </div>

          {/* Cells */}
          <div className="grid grid-cols-7 gap-1.5" data-testid="leave-calendar-grid">
            {monthCells.map((d, i) => {
              const inMonth = d.getMonth() === calMonth;
              const key = ymd(d);
              const dayLeaves = leavesByDay[key] || [];
              const approved = dayLeaves.filter(l => l.status === "approved");
              const pending = dayLeaves.filter(l => l.status === "pending");
              const dayHols = holidaysByDay[key] || [];
              const isToday = key === ymd(today);
              const isSelected = key === selectedDay;
              // Visual hierarchy:
              // - bank holiday OR approved leave → solid orange tint
              // - pending only → amber outline
              // - else → grey/neutral
              let cellClass = "bg-slate-50 border-slate-100";
              if (approved.length || dayHols.length) {
                cellClass = "bg-[#FFE9DC] border-[#F97316] text-[#9A3412]";
              } else if (pending.length) {
                cellClass = "bg-white border-2 border-amber-300";
              }
              return (
                <button type="button" key={i}
                  onClick={() => setSelectedDay(isSelected ? null : key)}
                  data-testid={`leave-cal-cell-${key}`}
                  className={`relative h-20 rounded-lg border text-left p-1.5 text-xs transition-colors ${cellClass} ${inMonth ? "" : "opacity-40"} ${isSelected ? "ring-2 ring-[#2563EB]" : ""}`}>
                  <div className={`text-[11px] font-medium ${isToday ? "text-[#2563EB]" : ""}`}>{d.getDate()}</div>
                  {dayHols.length > 0 && (
                    <div className="absolute top-1.5 right-1.5 text-[9px] uppercase tracking-widest font-medium text-[#9A3412]" title={dayHols.map(h=>h.name).join(", ")}>BH</div>
                  )}
                  <div className="mt-1 space-y-0.5 max-h-12 overflow-hidden">
                    {approved.slice(0, 2).map(lv => (
                      <div key={lv.leave_id} className="truncate" title={empName(lv.employee_id)}>
                        {isAdmin ? empName(lv.employee_id) : lv.category_name}
                      </div>
                    ))}
                    {approved.length > 2 && <div className="text-[10px] opacity-70">+{approved.length - 2} more</div>}
                    {!approved.length && pending.length > 0 && (
                      <div className="text-[10px] text-amber-800">{pending.length} pending</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Selected day detail */}
          {selectedDay && (
            <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4" data-testid="leave-cal-selected">
              <div className="font-display text-lg mb-2">{selectedDay}</div>
              {(holidaysByDay[selectedDay] || []).map(h => (
                <div key={h.holiday_id} className="text-sm text-[#9A3412] mb-1">
                  <span className="inline-block px-2 py-0.5 rounded-full bg-[#FFE9DC] text-[10px] uppercase tracking-widest font-medium mr-2">BH</span>
                  {h.name} <span className="text-xs text-[#4B5773]">· {h.country_code}</span>
                </div>
              ))}
              {(leavesByDay[selectedDay] || []).length > 0 ? (
                <ul className="divide-y divide-slate-100 mt-2">
                  {(leavesByDay[selectedDay] || []).map(lv => (
                    <li key={lv.leave_id} className="py-2 flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium">{isAdmin ? empName(lv.employee_id) : lv.category_name}</div>
                        <div className="text-xs text-[#4B5773]">{lv.category_name}{lv.half_day !== "none" ? ` · ${lv.half_day.toUpperCase()}` : ""}</div>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLOR[lv.status]}`}>{lv.status}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                !(holidaysByDay[selectedDay] || []).length && (
                  <div className="text-sm text-[#4B5773]">No leave or bank holiday on this day.</div>
                )
              )}
            </div>
          )}
        </Card>
      ) : (
      <Card>
        {/* Quick status filter — list view only, default "all". */}
        <div className="flex flex-wrap items-center gap-2 mb-4"
             data-testid="leave-status-filter">
          {FILTER_BUTTONS.map((b) => {
            const isActive = statusFilter === b.key;
            const count = statusCounts[b.key] ?? 0;
            return (
              <button
                key={b.key}
                type="button"
                onClick={() => setStatusFilter(b.key)}
                data-testid={`leave-filter-${b.key}`}
                aria-pressed={isActive}
                className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${isActive ? b.active : b.idle}`}
              >
                <span>{b.label}</span>
                <span
                  className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${isActive ? "bg-white/70" : "bg-slate-100 text-[#4B5773]"}`}
                  data-testid={`leave-filter-${b.key}-count`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#4B5773]">
              {isAdmin && <th className="py-3 eyebrow font-normal">Employee</th>}
              <th className="py-3 eyebrow font-normal">Type</th>
              <th className="py-3 eyebrow font-normal">Dates</th>
              <th className="py-3 eyebrow font-normal">Status</th>
              <th className="py-3 eyebrow font-normal">Notes</th>
              <th className="py-3 eyebrow font-normal text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredRows.map(lv => (
              <tr key={lv.leave_id} data-testid={`leave-row-${lv.leave_id}`}>
                {isAdmin && <td className="py-3 font-medium">{empName(lv.employee_id)}</td>}
                <td className="py-3">{lv.category_name}</td>
                <td className="py-3">{lv.start_date}{lv.end_date !== lv.start_date ? ` → ${lv.end_date}` : ""}{lv.half_day !== "none" && <span className="text-xs ml-1 text-[#4B5773]">({lv.half_day.toUpperCase()})</span>}</td>
                <td className="py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLOR[lv.status]}`}>{lv.status}</span>
                  {lv.status === "pending" && isAdmin && (
                    <span className="ml-2"><LeaveSimulationPill leaveRequestId={lv.leave_id} /></span>
                  )}
                  {lv.status === "pending" && isAdmin && pendingDraftIds.has(lv.leave_id) && (
                    <Link
                      to={`/app/drafts?kind=leave_decision&leave_id=${lv.leave_id}`}
                      className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-medium text-amber-900 px-1.5 py-0.5 rounded bg-amber-100 border border-amber-300 hover:bg-amber-200 align-middle"
                      data-testid={`leave-draft-pending-${lv.leave_id}`}
                      title="A leave-approval draft is awaiting review. Open the Drafts page to review it."
                    >
                      <Sparkles className="h-3 w-3" /> Draft pending
                    </Link>
                  )}
                </td>
                <td className="py-3 text-[#4B5773]">{lv.notes || "-"}</td>
                <td className="py-3 text-right">
                  {isAdmin && lv.status === "pending" && (
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={()=>decide(lv,"approved")} data-testid={`leave-approve-${lv.leave_id}`} className="text-emerald-700"><Check className="h-4 w-4"/></Button>
                      <Button size="sm" variant="ghost" onClick={()=>decide(lv,"rejected")} data-testid={`leave-reject-${lv.leave_id}`} className="text-slate-500"><X className="h-4 w-4"/></Button>
                    </div>
                  )}
                  {(lv.status === "pending" || isAdmin) && (
                    <Button size="sm" variant="ghost" onClick={()=>cancel(lv)} data-testid={`leave-cancel-${lv.leave_id}`} className="text-slate-500 ml-1">Cancel</Button>
                  )}
                </td>
              </tr>
            ))}
            {!filteredRows.length && (
              <tr>
                <td colSpan={isAdmin ? 6 : 5}
                    className="py-8 text-center text-[#4B5773]"
                    data-testid="leave-empty-state">
                  {rows.length === 0
                    ? "No leave requests yet."
                    : `No ${statusFilter} leave requests.`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{isAdmin ? "Add leave" : "Request leave"}</DialogTitle></DialogHeader>
          {isAdmin && form.employee_id && form.start_date && form.end_date && (
            <AdvisoryRowLeave
              employeeId={form.employee_id}
              startDate={form.start_date}
              endDate={form.end_date}
              leaveType="annual"
            />
          )}
          <form onSubmit={submit} className="space-y-4">
            {isAdmin && (
              <div>
                <Label className="eyebrow">Employee</Label>
                <Select required value={form.employee_id} onValueChange={(v)=>setForm({...form, employee_id: v})}>
                  <SelectTrigger data-testid="leave-emp-select" className="mt-2 h-11 rounded-lg"><SelectValue placeholder="Select employee"/></SelectTrigger>
                  <SelectContent>{employees.map(e=> <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="eyebrow">Leave type</Label>
              <Select required value={form.category_id} onValueChange={(v)=>setForm({...form, category_id: v})}>
                <SelectTrigger data-testid="leave-cat-select" className="mt-2 h-11 rounded-lg"><SelectValue placeholder="Select leave type"/></SelectTrigger>
                <SelectContent>{categories.map(c=> <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="eyebrow">From</Label>
                <Input required type="date" value={form.start_date} data-testid="leave-from-input"
                  onChange={(e)=>setForm({...form, start_date: e.target.value, end_date: form.end_date || e.target.value})}
                  className="mt-2 h-11 rounded-lg"/>
              </div>
              <div>
                <Label className="eyebrow">To</Label>
                <Input required type="date" value={form.end_date} data-testid="leave-to-input"
                  onChange={(e)=>setForm({...form, end_date: e.target.value})} className="mt-2 h-11 rounded-lg"/>
              </div>
            </div>
            <div>
              <Label className="eyebrow">Half day?</Label>
              <Select value={form.half_day} onValueChange={(v)=>setForm({...form, half_day: v})}>
                <SelectTrigger className="mt-2 h-11 rounded-lg" data-testid="leave-halfday-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No - full day(s)</SelectItem>
                  <SelectItem value="am">Morning (AM) only</SelectItem>
                  <SelectItem value="pm">Afternoon (PM) only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="eyebrow">Notes</Label>
              <Input value={form.notes} data-testid="leave-notes-input"
                onChange={(e)=>setForm({...form, notes: e.target.value})} className="mt-2 h-11 rounded-lg"/>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" data-testid="leave-cancel-btn" onClick={()=>setOpen(false)}
                className="rounded-lg border-slate-200">Close</Button>
              <Button type="submit" data-testid="leave-submit-btn" className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">{isAdmin ? "Add" : "Submit"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
